import React, { useState, useCallback } from 'react';
import { Select, MenuItem, FormControl } from '@mui/material';
import { HistoricalLayersService, HistoricalTileSource } from '../../../types/historical-layers';
import { getMapBoundsFromCenter } from '../../../utilities/map-utils';
import { BaseHistoricalLayerSelector } from './BaseHistoricalLayerSelector';

/**
 * Props for the HistoricalLayerSelector component
 */
export interface HistoricalLayerSelectorProps {
  /**
   * Currently selected year
   */
  selectedYear: number;

  /**
   * Callback when year selection changes
   */
  onYearChange: (year: number, tileSource: HistoricalTileSource) => void;

  /**
   * Service for managing historical layers
   */
  historicalLayersService: HistoricalLayersService;

  /**
   * Whether the selector is disabled
   */
  disabled?: boolean;

  /**
   * Current map center for location-based filtering
   */
  mapCenter?: [number, number]; // [lon, lat]
}

/**
 * React functional component for selecting historical satellite imagery layers
 * Uses a timeline slider for intuitive year selection
 * Fetches data based on map center location
 */
export const HistoricalLayerSelector: React.FC<HistoricalLayerSelectorProps> = ({
  selectedYear,
  onYearChange,
  historicalLayersService,
  disabled = false,
  mapCenter,
}) => {
  const [yearsToFetch, setYearsToFetch] = useState<number>(3); // Default to 3 years

  // Handle fetch data based on map location
  const handleFetchData = useCallback(
    async (forceRefresh: boolean = false) => {
      if (!mapCenter || !mapCenter[0] || !mapCenter[1]) {
        throw new Error('No map center available');
      }

      const bounds = getMapBoundsFromCenter(mapCenter[0], mapCenter[1]);
      // Update available years from backend
      await historicalLayersService.updateAvailableYears(
        mapCenter[0],
        mapCenter[1],
        bounds,
        yearsToFetch,
        undefined,
        forceRefresh,
      );
    },
    [mapCenter, historicalLayersService, yearsToFetch],
  );

  // Custom controls for years to fetch selector
  const customControls = (
    <FormControl size="small" sx={{ minWidth: 60 }}>
      <Select
        value={yearsToFetch}
        onChange={(e) => setYearsToFetch(e.target.value as number)}
        sx={{
          height: 32,
          fontSize: '0.875rem',
          '& .MuiSelect-select': {
            py: 0.5,
            px: 1,
          },
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <MenuItem key={num} value={num}>
            {num}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <BaseHistoricalLayerSelector
      selectedYear={selectedYear}
      onYearChange={onYearChange}
      historicalLayersService={historicalLayersService}
      disabled={disabled}
      onFetchData={handleFetchData}
      canFetch={!!mapCenter}
      customControls={customControls}
      noDataMessage="No historical imagery available at this location"
      noDataSubMessage="Try panning to a different area"
      fetchTooltip={`Fetch the ${yearsToFetch} most recent historical imagery year${yearsToFetch > 1 ? 's' : ''} for this location`}
    />
  );
};
